import React from 'react';
import { Text, Editor, Point, Range, Transforms, createEditor } from 'slate';
import { withReact } from 'nifty-slate-react';
import { withHistory } from 'slate-history';
import { jsx } from 'slate-hyperscript'

import IconFormatHeadingOne from '../icons/icon-format-heading-one';
import IconFormatHeadingTwo from '../icons/icon-format-heading-two';
import IconFormatHeadingThree from '../icons/icon-format-heading-three';
import IconFormatLineSeparator from '../icons/icon-format-line-separator';
import IconFormatBold from '../icons/icon-format-bold';
import IconFormatItalic from '../icons/icon-format-italic';
import IconFormatUnderline from '../icons/icon-format-underline';
import IconFormatStrike from '../icons/icon-format-strike';
import IconLink from '../icons/icon-link';
import IconFormatImage from '../icons/icon-format-image';
import IconFormatTable from '../icons/icon-format-table';
import IconFormatUnorderedList from '../icons/icon-format-unordered-list';
import IconFormatOrderedList from '../icons/icon-format-ordered-list';
import IconFormatBlockquote from '../icons/icon-format-blockquote';
import IconFormatTodo from '../icons/icon-format-todo';
import IconFormatCode from '../icons/icon-format-code';
import IconDiscussionAlt from '../icons/icon-discussion-alt';

export const NODE_TYPES = {
    paragraph: 'paragraph',
    headingOne: 'heading-one',
    headingTwo: 'heading-two',
    headingThree: 'heading-three',
    lineSeparator: 'line-separator',
    image: 'image',
    table: 'table',
    unorderedList: 'unordered-list',
    orderedList: 'ordered-list',
    blockQuote: 'block-quote',
    checklistItem: 'checklist-item',
    codeBlock: 'code-block',
    tableRow: 'table_row',
    tableCell: 'table_cell',
    listItem: 'list-item',
    deprecatedListItem: 'deprecated-list-item',
    bold: 'bold',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'strikethrough',
    link: 'link',
    code: 'code',
    comment: 'comment'
};

const NODES = [
    {
        id: 'paragraph',
        type: NODE_TYPES.paragraph,
        group: 'block',
        label: 'Paragraph',
        tag: 'p'
    },
    {
        id: 'headingOne',
        type: NODE_TYPES.headingOne,
        group: 'block',
        label: 'Heading 1',
        icon: <IconFormatHeadingOne />,
        tag: 'h1'
    },
    {
        id: 'headingTwo',
        type: NODE_TYPES.headingTwo,
        group: 'block',
        label: 'Heading 2',
        icon: <IconFormatHeadingTwo />,
        tag: 'h2'
    },
    {
        id: 'headingThree',
        type: NODE_TYPES.headingThree,
        group: 'block',
        label: 'Heading 3',
        icon: <IconFormatHeadingThree />,
        tag: 'h3'
    },
    {
        id: 'lineSeparator',
        type: NODE_TYPES.lineSeparator,
        group: 'block',
        label: 'Line Separator',
        icon: <IconFormatLineSeparator />,
        tag: 'hr'
    },
    {
        id: 'image',
        type: NODE_TYPES.image,
        group: 'block',
        label: 'Image',
        icon: <IconFormatImage />,
        tag: 'img'
    },
    {
        id: 'table',
        type: NODE_TYPES.table,
        group: 'block',
        label: 'Table',
        icon: <IconFormatTable />,
        tag: 'table'
    },
    {
        id: 'tableRow',
        type: NODE_TYPES.tableRow,
        group: 'block',
        label: 'Table Row',
        tag: 'tr'
    },
    {
        id: 'tableCell',
        type: NODE_TYPES.tableCell,
        group: 'block',
        label: 'Table Cell',
        tag: 'td'
    },
    {
        id: 'unorderedList',
        type: NODE_TYPES.unorderedList,
        group: 'block',
        label: 'Bulleted List',
        icon: <IconFormatUnorderedList />,
        tag: 'ul'
    },
    {
        id: 'orderedList',
        type: NODE_TYPES.orderedList,
        group: 'block',
        label: 'Ordered List',
        icon: <IconFormatOrderedList />,
        tag: 'ol'
    },
    {
        id: 'blockQuote',
        type: NODE_TYPES.blockQuote,
        group: 'block',
        label: 'Quote',
        icon: <IconFormatBlockquote />,
        tag: 'blockquote'
    },
    {
        id: 'codeBlock',
        type: NODE_TYPES.codeBlock,
        group: 'block',
        label: 'Code',
        icon: <IconFormatCode />,
        tag: 'pre'
    },
    {
        id: 'listItem',
        type: NODE_TYPES.listItem,
        group: 'block',
        label: 'List Item',
        tag: 'li'
    },
    {
        id: 'checklistItem',
        type: NODE_TYPES.checklistItem,
        group: 'block',
        label: 'To-do',
        icon: <IconFormatTodo />,
        tag: 'li'
    },
    {
        id: 'link',
        type: NODE_TYPES.link,
        group: 'block',
        label: 'Link',
        inline: true,
        icon: <IconLink />,
        tag: 'a'
    },
    {
        id: 'comment',
        type: NODE_TYPES.comment,
        group: 'block',
        label: 'Add Comment...',
        inline: true,
        icon: <IconDiscussionAlt />,
        tag: 'span'
    },
    {
        id: 'bold',
        type: NODE_TYPES.bold,
        group: 'mark',
        label: 'Bold',
        icon: <IconFormatBold />,
        tag: 'strong'
    },
    {
        id: 'italic',
        type: NODE_TYPES.italic,
        group: 'mark',
        label: 'Italic',
        icon: <IconFormatItalic />,
        tag: 'em'
    },
    {
        id: 'underline',
        type: NODE_TYPES.underline,
        group: 'mark',
        label: 'Underline',
        icon: <IconFormatUnderline />,
        tag: 'u'
    },
    {
        id: 'strikethrough',
        type: NODE_TYPES.strikethrough,
        group: 'mark',
        label: 'Strike',
        icon: <IconFormatStrike />,
        tag: 's'
    },
    {
        id: 'code',
        type: NODE_TYPES.code,
        group: 'mark',
        label: 'Code',
        icon: <IconFormatCode />,
        tag: 'code'
    }
];

const TOOLBAR_MENU_EDIT_ITEMS = [
    [NODE_TYPES.headingOne, NODE_TYPES.headingTwo, NODE_TYPES.headingThree, NODE_TYPES.lineSeparator],
    [NODE_TYPES.bold, NODE_TYPES.italic, NODE_TYPES.underline, NODE_TYPES.strikethrough, NODE_TYPES.link],
    [NODE_TYPES.image, NODE_TYPES.table, NODE_TYPES.unorderedList, NODE_TYPES.orderedList, NODE_TYPES.blockQuote, NODE_TYPES.checklistItem, NODE_TYPES.codeBlock],
    [NODE_TYPES.comment]
]

const CONTEXT_MENU_EDIT_ITEMS = [
    [NODE_TYPES.headingOne, NODE_TYPES.headingTwo, NODE_TYPES.headingThree, NODE_TYPES.lineSeparator],
    [NODE_TYPES.bold, NODE_TYPES.italic, NODE_TYPES.underline, NODE_TYPES.strikethrough, NODE_TYPES.link],
    [NODE_TYPES.unorderedList, NODE_TYPES.orderedList, NODE_TYPES.blockQuote, NODE_TYPES.checklistItem],
    [NODE_TYPES.comment]
];
const BLOCK_CHOOSER_EDIT_ITEMS = [
    [NODE_TYPES.headingOne, NODE_TYPES.headingTwo, NODE_TYPES.headingThree, NODE_TYPES.lineSeparator],
    [NODE_TYPES.image, NODE_TYPES.table, NODE_TYPES.unorderedList, NODE_TYPES.orderedList, NODE_TYPES.blockQuote, NODE_TYPES.checklistItem, NODE_TYPES.codeBlock]
];

const TOOLBAR_MENU_COMMENT_ITEMS = [ [NODE_TYPES.comment] ];
const CONTEXT_MENU_COMMENT_ITEMS = [ [NODE_TYPES.comment] ];
const BLOCK_CHOOSER_COMMENT_ITEMS = [ [NODE_TYPES.comment] ];

const TOOLBAR_MENU_VIEW_ITEMS = [];
const CONTEXT_MENU_VIEW_ITEMS = [];
const BLOCK_CHOOSER_VIEW_ITEMS = [];

export const getContextMenuItems = permission => {
    switch (permission) {
        case 'edit':
            return CONTEXT_MENU_EDIT_ITEMS;

        case 'comment':
            return CONTEXT_MENU_COMMENT_ITEMS;

        default:
            return CONTEXT_MENU_VIEW_ITEMS;
    }
}

export const getBlockChooserItems = permission => {
    switch (permission) {
        case 'edit':
            return BLOCK_CHOOSER_EDIT_ITEMS;

        case 'comment':
            return BLOCK_CHOOSER_COMMENT_ITEMS;

        default:
            return BLOCK_CHOOSER_VIEW_ITEMS;
    }
}

export const getToolbarMenuItems = permission => {
    switch (permission) {
        case 'edit':
            return TOOLBAR_MENU_EDIT_ITEMS;

        case 'comment':
            return TOOLBAR_MENU_COMMENT_ITEMS;

        default:
            return TOOLBAR_MENU_VIEW_ITEMS;
    }
}

export const getNodeByType = type => NODES.find(node => node.type === type);
const getNodeByTag = tag => NODES.find(node => node.tag === tag);

const withTables = editor => {
    const { deleteBackward, deleteForward, insertBreak } = editor;

    editor.deleteBackward = unit => {
        const { selection } = editor

        if (selection && Range.isCollapsed(selection)) {
            const [el] = Editor.nodes(editor, {
                match: n => n.type
            });

            const [cell] = Editor.nodes(editor, {
                match: n => n.type === NODE_TYPES.tableCell
            });

            if (selection.anchor.offset === 0) {
                const before = Editor.before(editor, el[1]);

                try {
                    const [prevParent] = before && Editor.parent(editor, before, {
                        depth: 2
                    });

                    if (prevParent && prevParent.type === NODE_TYPES.table) {
                        return;
                    }
                } catch(e) {}
            }

            if (cell) {
                const [, cellPath] = cell
                const start = Editor.start(editor, cellPath)

                if (Point.equals(selection.anchor, start)) {
                    return;
                }
            }
        }

        deleteBackward(unit)
    }

    editor.deleteForward = unit => {
        const { selection } = editor;

        if (selection && Range.isCollapsed(selection)) {
            const [el] = Editor.nodes(editor, {
                match: n => n.type
            });

            const [cell] = Editor.nodes(editor, {
                match: n => n.type === NODE_TYPES.tableCell
            });

            const [, currentNodeEnd] = Editor.edges(editor, el[1]);

            if (selection.anchor.offset === currentNodeEnd.offset) {
                const after = Editor.after(editor, el[1]);
                const parentNode = after && Editor.parent(editor, after, {
                    depth: 2
                });

                if (!parentNode) {
                    return;
                }

                const [nextParent] = parentNode;

                if (nextParent && nextParent.type === NODE_TYPES.table) {
                    return;
                }
            }

            if (cell) {
                const [, cellPath] = cell;
                const end = Editor.end(editor, cellPath);

                if (Point.equals(selection.anchor, end)) {
                    return;
                }
            }
        }

        deleteForward(unit)
    }

    editor.insertBreak = () => {
        const { selection } = editor

        if (selection) {
            const [table] = Editor.nodes(editor, { match: n => n.type === NODE_TYPES.table })

            if (table) {
                return
            }
        }

        insertBreak();
    }

    return editor
};

export const niftyDoc = ({ onOperation } = {}) => {
    const editor = withTables(withHistory(withReact(createEditor())));
    const { isInline, insertData, apply } = editor;

    editor.isInline = element => {
        const node = getNodeByType(element.type);
        return node ? Boolean(node.inline) : isInline(element);
    };

    editor.insertData = data => {
        const html = data.getData('text/html');

        if (html) {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const fragment = deserialize(parsed.body);

            if (fragment.length !== 1 || fragment.children || fragment.type || !fragment.text) {
                Transforms.insertFragment(editor, fragment);
            } else {
                Transforms.insertNodes(editor, {
                    children: fragment
                });
            }
            return;
        }

        insertData(data);
    };

    editor.apply = op => {
        onOperation && onOperation(op);
        apply(op);
    };

    editor.originalApply = apply;

    return editor;
}

export const isNodeEmpty = node => {
    const nodeEmpty = !node.text || node.text === '';
    return nodeEmpty && (!node.children || node.children.every(node => isNodeEmpty(node)));
};

export const getNodesByType = (editor, types) => {
    if (!editor.children.length) {
        return [];
    }

    if (typeof types === 'string') {
        types = [types];
    }

    const [...nodes] = Editor.nodes(editor, {
        at: [],
        match: n => types.includes(n.type)
    });

    return nodes;
}

export const isMarkActive = (editor, type) => {
    const marks = Editor.marks(editor);
    return marks ? marks[type] === true : false;
}

export const isBlockActive = (editor, type) => {
    const [match] = Editor.nodes(editor, {
        match: n => n.type === type
    });
    return !!match;
}

export const createTableCell = () => ({
    type: NODE_TYPES.tableCell,
    children: [{ text: '' }]
});

export const createTableRow = cols => {
    const row = {
        type: NODE_TYPES.tableRow,
        children: []
    };

    for (let i = 0; i < cols; i++) {
        const cell = createTableCell();

        row.children.push(cell);
    }

    return row;
};

export const createTableNode = (rows = 2, cols = 3) => {
    const node = {
        type: NODE_TYPES.table,
        children: []
    };

    for (let i = 0; i < rows; i++) {
        node.children.push(createTableRow(cols));
    }

    return node;
}

export const serialize = node => {
    if (Text.isText(node)) {
        return node.text;
    }

    const children = node.children ? node.children.map(n => serialize(n)).join('') : '';

    const nodeProps = getNodeByType(node.type);

    if (!nodeProps) {
        return children;
    }

    switch (node.type) {
        case NODE_TYPES.image:
            return `<p><img src="${node.data.fileUrl}" alt="${children}" /></p>`;

        case NODE_TYPES.checklistItem:
            return `<li><input type="checkbox" checked="${node.data && node.data.checked}" /> ${children}</li>`;

        case NODE_TYPES.link:
            return `<a href=${node.data && node.data.href}>${children}</a>`;

        default:
            return `<${nodeProps.tag}>${children}</${nodeProps.tag}>`;
    }
}

export const deserialize = el => {
    if (el.nodeType === 3) {
        return el.textContent;
    } else if (el.nodeType !== 1) {
        return null;
    } else if (el.nodeName === 'BR') {
        return '\n';
    }

    const { nodeName } = el;
    let parent = el;

    if (
        nodeName === 'PRE' &&
        el.childNodes[0] &&
        el.childNodes[0].nodeName === 'CODE'
    ) {
        parent = el.childNodes[0];
    }

    const children = Array.from(parent.childNodes)
        .map(deserialize)
        .flat();

    if (el.nodeName === 'BODY') {
        return jsx('fragment', {}, children);
    }

    const node = getNodeByTag(nodeName.toLowerCase());

    if (node) {
        const attrs = {
            type: node.type,
            data: {}
        }

        switch (node.type) {
            case NODE_TYPES.link:
                attrs.data.href = el.getAttribute('href');
                return jsx('element', attrs, children);

            case NODE_TYPES.image:
                attrs.data.fileUrl = el.getAttribute('src');
                return jsx('element', attrs, [el.getAttribute('alt')]);

            case NODE_TYPES.comment:
                return children;

            default:
                if (node.group === 'block') {
                    return jsx('element', attrs, children);
                }

                return children.map(child => jsx('text', {
                    [node.type]: true
                }, child));
        }
    }

    return children;
}
